import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ImagePlus, X } from "lucide-react";

interface ImageUploaderProps {
  files: File[];
  setFiles: (files: File[]) => void;
  maxFiles?: number;
  accept?: string;
}

export function ImageUploader({
  files,
  setFiles,
  maxFiles = 25,
  accept = "image/*"
}: ImageUploaderProps) {
  const { t } = useTranslation();
  const isVideo = accept.includes("video");

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (files.length + acceptedFiles.length > maxFiles) {
      return;
    }
    setFiles([...files, ...acceptedFiles]);
  }, [files, maxFiles, setFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept ? { [accept]: [] } : undefined,
    maxFiles: maxFiles - files.length
  });

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary"}`}
      >
        <input {...getInputProps()} />
        <ImagePlus className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {isDragActive
            ? isVideo ? t("dropVideosHere") : t("dropImagesHere")
            : isVideo ? t("dragAndDropVideos") : t("dragAndDropImages")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
        >
          {isVideo ? t("selectVideos") : t("selectImages")}
        </Button>
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {files.map((file, index) => (
            <div key={index} className="relative group">
              {isVideo ? (
                <video
                  src={URL.createObjectURL(file)}
                  className="w-full h-32 object-cover rounded-lg"
                  controls
                />
              ) : (
                <img
                  src={URL.createObjectURL(file)}
                  alt=""
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeFile(index)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
